<!-- eslint-disable vue/no-bare-strings-in-template -->
<script>
import Avatar from 'next/avatar/Avatar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import contactsAPI from 'dashboard/api/contacts';
import messageAPI from 'dashboard/api/inbox/message';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'ForwardContactModal',
  components: {
    Avatar,
    Spinner,
    Icon,
    NextButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
  },
  emits: ['close'],
  data() {
    return {
      searchQuery: '',
      contacts: [],
      isLoading: false,
      isForwarding: false,
      searchTimeout: null,
      currentPage: 1,
      hasMore: true,
      isLoadingMore: false,
      selectedIds: new Set(),
      isSearchMode: false,
    };
  },
  computed: {
    selectedCount() {
      return this.selectedIds.size;
    },
    allSelected() {
      return (
        this.contacts.length > 0 &&
        this.contacts.every(c => this.selectedIds.has(c.id))
      );
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.reset();
        this.loadInitialContacts();
      }
    },
    searchQuery(value) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.contacts = [];
        this.currentPage = 1;
        this.hasMore = true;
        this.selectedIds = new Set();
        if (value.trim()) {
          this.isSearchMode = true;
          this.fetchSearchContacts(value.trim(), 1);
        } else {
          this.isSearchMode = false;
          this.loadInitialContacts();
        }
      }, 300);
    },
  },
  methods: {
    reset() {
      this.searchQuery = '';
      this.contacts = [];
      this.currentPage = 1;
      this.hasMore = true;
      this.selectedIds = new Set();
      this.isSearchMode = false;
    },
    onClose() {
      this.$emit('close');
    },
    async loadInitialContacts() {
      this.isLoading = true;
      try {
        const response = await contactsAPI.get(1);
        const payload = response.data?.payload || [];
        this.contacts = payload;
        this.currentPage = 1;
        this.hasMore = payload.length >= 15;
      } catch {
        this.contacts = [];
      } finally {
        this.isLoading = false;
      }
    },
    async fetchSearchContacts(query, page) {
      if (page === 1) {
        this.isLoading = true;
      } else {
        this.isLoadingMore = true;
      }
      try {
        const response = await contactsAPI.search(query, page);
        const payload = response.data?.payload || [];
        if (page === 1) {
          this.contacts = payload;
        } else {
          this.contacts = [...this.contacts, ...payload];
        }
        this.hasMore = payload.length >= 15;
        this.currentPage = page;
      } catch {
        if (page === 1) this.contacts = [];
      } finally {
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    },
    async loadMore() {
      if (this.isLoadingMore || !this.hasMore || this.isLoading) return;
      const nextPage = this.currentPage + 1;
      this.isLoadingMore = true;
      try {
        let response;
        if (this.isSearchMode) {
          response = await contactsAPI.search(
            this.searchQuery.trim(),
            nextPage
          );
        } else {
          response = await contactsAPI.get(nextPage);
        }
        const payload = response.data?.payload || [];
        this.contacts = [...this.contacts, ...payload];
        this.hasMore = payload.length >= 15;
        this.currentPage = nextPage;
      } catch {
        // silently ignore load-more errors
      } finally {
        this.isLoadingMore = false;
      }
    },
    onScroll(event) {
      const el = event.target;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      if (nearBottom) {
        this.loadMore();
      }
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedIds = new Set();
      } else {
        this.selectedIds = new Set(this.contacts.map(c => c.id));
      }
    },
    toggleSelect(contactId) {
      const next = new Set(this.selectedIds);
      if (next.has(contactId)) {
        next.delete(contactId);
      } else {
        next.add(contactId);
      }
      this.selectedIds = next;
    },
    isSelected(contactId) {
      return this.selectedIds.has(contactId);
    },
    async forwardContact(contact) {
      if (this.isForwarding) return;
      this.isForwarding = true;
      try {
        await messageAPI.forwardContact(this.conversationId, contact.id);
        useAlert(this.$t('CONVERSATION.FORWARD_CONTACT.SUCCESS'));
        this.onClose();
      } catch {
        useAlert(this.$t('CONVERSATION.FORWARD_CONTACT.ERROR'));
      } finally {
        this.isForwarding = false;
      }
    },
    async forwardSelected() {
      if (this.isForwarding || this.selectedIds.size === 0) return;
      this.isForwarding = true;
      const ids = Array.from(this.selectedIds);
      try {
        await Promise.all(
          ids.map(id => messageAPI.forwardContact(this.conversationId, id))
        );
        useAlert(
          this.$t('CONVERSATION.FORWARD_CONTACT.SUCCESS_MULTIPLE', {
            count: ids.length,
          })
        );
        this.onClose();
      } catch {
        useAlert(this.$t('CONVERSATION.FORWARD_CONTACT.ERROR'));
      } finally {
        this.isForwarding = false;
      }
    },
    contactSubtext(contact) {
      const parts = [];
      if (contact.phone_number) parts.push(contact.phone_number);
      if (contact.email) parts.push(contact.email);
      return parts.join(' · ');
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="flex flex-col h-[520px]">
      <woot-modal-header
        :header-title="$t('CONVERSATION.FORWARD_CONTACT.TITLE')"
      />

      <div class="flex-1 overflow-hidden flex flex-col px-8 pb-6">
        <!-- Search -->
        <div class="relative mb-3">
          <Icon
            icon="i-lucide-search"
            class="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-10"
          />
          <input
            v-model="searchQuery"
            type="text"
            :aria-label="$t('CONVERSATION.FORWARD_CONTACT.SEARCH')"
            :placeholder="$t('CONVERSATION.FORWARD_CONTACT.SEARCH')"
            class="w-full !pl-9 !pr-3 !py-2 !m-0 rounded-[10px] border-0 outline outline-1 outline-n-weak bg-n-alpha-black2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand transition-colors"
          />
        </div>

        <!-- Select All row -->
        <div
          v-if="contacts.length > 0"
          class="flex items-center gap-2 px-3 py-2 mb-1 rounded-lg hover:bg-n-alpha-1 cursor-pointer"
          @click="toggleSelectAll"
        >
          <input
            type="checkbox"
            :checked="allSelected"
            class="size-4 rounded accent-n-brand cursor-pointer"
            @click.stop="toggleSelectAll"
          />
          <span class="text-sm text-n-slate-11 select-none">
            {{ $t('CONVERSATION.FORWARD_CONTACT.SELECT_ALL') }}
          </span>
        </div>

        <!-- List -->
        <div
          class="flex-1 overflow-y-auto rounded-xl bg-n-alpha-black2"
          @scroll="onScroll"
        >
          <!-- Loading initial -->
          <div v-if="isLoading" class="flex items-center justify-center h-full">
            <Spinner :size="20" class="text-n-brand" />
          </div>

          <!-- Empty -->
          <div
            v-else-if="contacts.length === 0"
            class="flex flex-col items-center justify-center h-full px-4"
          >
            <Icon icon="i-lucide-users" class="size-6 text-n-slate-9 mb-2" />
            <p class="text-sm text-n-slate-11 mb-0.5">
              {{ $t('CONVERSATION.FORWARD_CONTACT.EMPTY') }}
            </p>
            <p class="text-xs text-n-slate-10 text-center">
              {{ $t('CONVERSATION.FORWARD_CONTACT.EMPTY_DESCRIPTION') }}
            </p>
          </div>

          <!-- Contacts -->
          <div v-else>
            <div
              v-for="contact in contacts"
              :key="contact.id"
              class="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-n-alpha-1 transition-colors border-b border-n-weak last:border-b-0"
            >
              <!-- Checkbox -->
              <input
                type="checkbox"
                :checked="isSelected(contact.id)"
                class="size-4 rounded accent-n-brand cursor-pointer shrink-0"
                @change="toggleSelect(contact.id)"
              />

              <!-- Avatar + info (click = quick single send) -->
              <button
                type="button"
                :disabled="isForwarding"
                :aria-label="contact.name"
                class="flex-1 flex items-center gap-3 text-left disabled:opacity-50 disabled:cursor-not-allowed min-w-0"
                @click="forwardContact(contact)"
              >
                <Avatar
                  :src="contact.thumbnail || ''"
                  :name="contact.name || 'Unknown'"
                  :size="32"
                  rounded
                />
                <div class="flex-1 min-w-0">
                  <p class="font-medium text-sm text-n-slate-12 truncate mb-0">
                    {{ contact.name }}
                  </p>
                  <p
                    v-if="contactSubtext(contact)"
                    class="text-xs text-n-slate-10 truncate mb-0"
                  >
                    {{ contactSubtext(contact) }}
                  </p>
                </div>
                <Icon
                  icon="i-lucide-send"
                  class="size-3.5 text-n-slate-10 shrink-0"
                />
              </button>
            </div>

            <!-- Load more spinner -->
            <div
              v-if="isLoadingMore"
              class="flex items-center justify-center py-3"
            >
              <Spinner :size="16" class="text-n-brand" />
            </div>
          </div>
        </div>

        <!-- Send selected footer -->
        <div v-if="selectedCount > 0" class="mt-3 flex justify-end">
          <NextButton
            :label="
              $t('CONVERSATION.FORWARD_CONTACT.SEND_SELECTED', {
                count: selectedCount,
              })
            "
            :disabled="isForwarding"
            :loading="isForwarding"
            @click="forwardSelected"
          />
        </div>
      </div>
    </div>
  </woot-modal>
</template>
